/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable camelcase */

import React, { useEffect } from 'react';
import { capitalize } from 'lodash';
import { createStyles, makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Theme,
  Typography,
} from '@material-ui/core';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import {
  GetBillingPackageOptions,
  GetBillingPackageOptionsVariables,
  GetBillingPackageOptions_billingPackageOptions as BillingPackageOptionType,
} from './__generated__/GetBillingPackageOptions';
import { GET_PRICES, GET_PRICE_PERIODS } from './price-model.gql';
import { useDeepEffect } from '../../../hooks/useDeepEffect';
import { GetBillingPackagePeriods } from './__generated__/GetBillingPackagePeriods';
import { useCampfireLazyQuery } from '../../../global/network/useCampfireLazyQuery';
import { useApiUrl } from '../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../global/network/useCampfireFetch';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      borderRadius: 8,
      backgroundColor: 'white',
      padding: '48px 36px',
      width: 905,
      border: 'none',
      boxShadow: 'rgba(35, 63, 77, 0.1) 0px 8px 24px 8px',
    },
    title: {
      fontWeight: 900,
      fontSize: 24,
    },
    subTitle: {
      fontWeight: 400,
      fontSize: 16,
      color: theme.color.grey.neutral400,
      marginTop: 16,
    },
    periods: {
      textTransform: 'none',
      borderRadius: 8,
      height: 45,
      paddingLeft: 20,
      paddingRight: 20,
    },
    priceContainer: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      gap: '20px',
      justifyContent: 'center',
    },
    price: {
      marginBottom: -24,
      marginLeft: -24,
      marginRight: -66,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'left',
      paddingLeft: 24,
      height: 95,
      borderTop: '1px solid lightgrey',
      color: theme.color.grey.neutral500,
      '& .number': {
        fontWeight: 800,
        fontSize: 30,
      },
      '& .period': {
        fontWeight: 700,
        fontSize: 20,
      },
      borderBottomLeftRadius: 12,
      borderBottomRightRadius: 12,
    },
    priceActive: {
      color: '#fcfcfc',
      backgroundColor: '#7570EA',
    },
    actions: {
      marginTop: 60,
      justifyContent: 'end',
      gap: '16px',
    },
    actionsButton: {
      height: 38,
      textTransform: 'none',
      borderRadius: 8,
      color: 'white',
    },
  })
);

export function SubscriptionOptions() {
  const { data } = useCampfireQuery<GetBillingPackagePeriods, {}>(GET_PRICE_PERIODS, {});
  const [getBillingPackageOption, { loading }] = useCampfireLazyQuery<
    GetBillingPackageOptions,
    GetBillingPackageOptionsVariables
  >(GET_PRICES);
  const [activePeriod, setActivePeriod] = React.useState<string>();
  const [billingPackageOptions, setBillingPackageOptions] = React.useState<{
    [p: string]: BillingPackageOptionType[] | undefined;
  }>({});
  const [selectedPrice, setSelectedPrice] = React.useState<string>('');
  const billingPackagePeriods = React.useMemo(() => {
    return data?.billingPackagePeriods ?? [];
  }, [data]);
  const options = React.useMemo(() => {
    if (activePeriod) {
      return billingPackageOptions[activePeriod] ?? [];
    }
    return [];
  }, [activePeriod, billingPackageOptions]);
  const classes = useStyles();
  useEffect(() => {
    if (activePeriod) {
      getBillingPackageOption({ variables: { period: activePeriod } }).then((response) => {
        setBillingPackageOptions((currentData) => ({
          ...currentData,
          [activePeriod]: response.data?.billingPackageOptions,
        }));
      });
    }
  }, [activePeriod]);

  useDeepEffect(() => {
    if (billingPackagePeriods.length > 0) {
      setActivePeriod(billingPackagePeriods[0]);
    }
  }, [billingPackagePeriods]);

  useEffect(() => {
    if (options.length > 0) {
      setSelectedPrice(options[0].billingPackageOptionId);
    }
  }, [options]);

  const apiUrl = useApiUrl();
  const campfireCheckout = useCampfireFetch<{ url: string }>({ defer: true });

  const onCheckout = () => {
    campfireCheckout
      .run({
        url: `${apiUrl}/tm/subscriptions/checkout`,
        method: 'post',
        data: {
          priceId: selectedPrice,
        },
      })
      .then((response) => {
        const { url } = response.data.data;
        window.location.replace(url);
      });
  };

  return (
    <Card className={classes.root}>
      <CardContent>
        <Typography variant='h3' className={classes.title}>
          Your Plan & Payment
        </Typography>
        <Typography className={classes.subTitle}>
          Please review your plan and select a billing cycle below, and continue to payment.
        </Typography>
        <Box display='flex' justifyContent='center' style={{ paddingTop: '25px' }}>
          <ButtonGroup variant='outlined' color='primary' aria-label='contained primary button group'>
            {billingPackagePeriods.map((period) => (
              <Button
                key={period}
                onClick={() => setActivePeriod(period)}
                classes={{ root: classes.periods }}
                variant={activePeriod === period ? 'contained' : 'outlined'}
              >
                {period
                  .split('-')
                  .map(capitalize)
                  .join(' ')}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
        <Box mt='25px'>
          {!loading ? (
            activePeriod ? (
              <RadioGroup
                onChange={(_unusedEvent, value) => setSelectedPrice(value)}
                value={selectedPrice}
                className={classes.priceContainer}
              >
                {options.map((billingPackageOption) => {
                  const stripePrice = JSON.parse(billingPackageOption.stripePrice || '{}');
                  const stripeProduct = JSON.parse(billingPackageOption.billingPackage.stripeProduct || '{}');
                  const isActive = billingPackageOption.billingPackageOptionId === selectedPrice;
                  const priceClassName = isActive ? `${classes.price} ${classes.priceActive}` : classes.price;
                  return (
                    <FormControlLabel
                      value={billingPackageOption.billingPackageOptionId}
                      control={<Radio color='primary' />}
                      label={
                        <div>
                          <h2 style={{ marginTop: -5 }}>{stripeProduct.name}</h2>
                          <p>TEXT</p>
                          <p>Feature One</p>
                          <p>Feature Two</p>
                          <p>Feature Three</p>
                          <p>Feature Four</p>
                          <div className={priceClassName}>
                            <span className='number'>${stripePrice.unit_amount / 100}</span>
                            <span style={{ paddingLeft: '8px' }}>{' / '}</span>
                            <span className='period' style={{ marginTop: '-2px', paddingLeft: '3px' }}>
                              {stripePrice?.recurring?.interval_count === 1
                                ? ' '
                                : stripePrice?.recurring?.interval_count}{' '}
                              {stripePrice?.recurring?.interval}
                            </span>
                          </div>
                        </div>
                      }
                      labelPlacement='start'
                      style={{
                        border: '1px solid lightgrey',
                        borderRadius: '12px',
                        padding: '24px',
                        alignItems: 'flex-start',
                        width: 250,
                      }}
                    />
                  );
                })}
              </RadioGroup>
            ) : null
          ) : (
            <Box display='flex'>
              <CircularProgress style={{ margin: 'auto' }} />
            </Box>
          )}
        </Box>
      </CardContent>
      <CardActions className={classes.actions}>
        <Button
          className={classes.actionsButton}
          variant='contained'
          style={{
            backgroundColor: '#9E9E9E',
          }}
        >
          Back
        </Button>
        <Button onClick={onCheckout} className={classes.actionsButton} variant='contained' color='primary'>
          Continue to Payment
        </Button>
      </CardActions>
    </Card>
  );
}
